/**
 * Display-friendly names for meeting platforms.
 */
export enum MeetingPlatform {
  GoogleMeet = "Google Meet",
  Zoom = "Zoom",
  MicrosoftTeams = "Microsoft Teams",
  Slack = "Slack",
  Other = "Meeting",
}

/**
 * Calendar providers for determining which calendar icon to display.
 */
export enum CalendarProvider {
  Google = "google",
  Outlook = "outlook",
  Other = "other",
}

/**
 * Analytics-friendly identifiers for meeting providers.
 */
export type MeetingProvider = "google_meet" | "zoom" | "teams" | "other";

/**
 * Returns the display-friendly platform name for a meeting URL.
 */
export const getMeetingPlatformFromUrl = (url: string | null | undefined): MeetingPlatform => {
  if (!url) return MeetingPlatform.Other;

  if (/zoom\.us/i.test(url)) {
    return MeetingPlatform.Zoom;
  }

  if (/meet\.google\.com/i.test(url)) {
    return MeetingPlatform.GoogleMeet;
  }

  if (/teams\.(microsoft\.com|live\.com)/i.test(url)) {
    return MeetingPlatform.MicrosoftTeams;
  }

  if (/slack\.com/i.test(url)) {
    return MeetingPlatform.Slack;
  }

  return MeetingPlatform.Other;
};

/**
 * Returns the analytics-friendly provider identifier for a meeting URL.
 */
export const getMeetingProvider = (url: string | null | undefined): MeetingProvider => {
  const platform = getMeetingPlatformFromUrl(url);
  switch (platform) {
    case MeetingPlatform.GoogleMeet:
      return "google_meet";
    case MeetingPlatform.Zoom:
      return "zoom";
    case MeetingPlatform.MicrosoftTeams:
      return "teams";
    default:
      return "other";
  }
};

/**
 * Returns the calendar provider based on the calendar event HTML link.
 */
export const getCalendarProviderFromUrl = (url: string | null | undefined): CalendarProvider => {
  if (!url) return CalendarProvider.Other;

  if (/calendar\.google\.com/i.test(url)) {
    return CalendarProvider.Google;
  }

  if (/outlook\.(office365\.com|live\.com|office\.com)/i.test(url)) {
    return CalendarProvider.Outlook;
  }

  return CalendarProvider.Other;
};
